import { Box, Flex, Heading, VStack, Text } from '@chakra-ui/react';
import { AnimatePresence } from 'framer-motion';
import React, { useEffect, useState, useCallback } from 'react';

import useIsMobile from 'lib/hooks/useIsMobile';
import LinkInternal from 'ui/shared/links/LinkInternal';

import LatestTasksItem from './LatestTasksItem';

interface Task {
  _id: string;
  address: string;
  source_address: string;
  source_info: {
    client: {
      client_address: string;
    };
    image_content: {
      compose1: string;
      compose2: string;
      compose3: string;
      compose4: string;
      name: string;
      description: string;
      logo_url: string;
      tags: Array<string>;
    };
    source_constraints: {
      maxPayment: number;
      minPayment: number;
      minAvailableStake: number;
      maxDuration: number;
      minTier: number;
    };
    _id: string;
    nexus_address: string;
    source_address: string;
    image_metadata_url: string;
    source_constraints_url: string;
    registered_providers: Array<{
      provider_address: string;
      _id: string;
    }>;
    tasks: Array<{
      task_address: string;
      _id: string;
    }>;
    status: string;
    timestamp: number;
    createdAt: string;
    updatedAt: string;
  };
  expiry_time: number;
  config_url: string;
  payment: string;
  offered_reward: string;
  attempted_providers: Array<{
    response?: {
      response_address: string;
    };
    provider_address: string;
    timestamp: number;
    _id: string;
    response_data?: {
      _id: string;
      response_address: string;
      task_address: string;
      provider_address: string;
      timestamp: number;
      response_url: string;
      status: string;
      createdAt: string;
      updatedAt: string;
    };
  }>;
  status: string;
  timestamp: number;
  createdAt: string;
  updatedAt: string;
}

interface TaskResponse {
  status: string;
  message: string;
  data:
    | Array<Task>
    | {
      tasks: Array<Task>;
    };
  tasks?: Array<Task>; // Optional for backward compatibility
}

const LatestTasks = () => {
  const isMobile = useIsMobile();
  const [ isLoading, setIsLoading ] = useState(true);
  const [ tasks, setTasks ] = useState<Array<Task>>([]);
  const maxDisplayCount = isMobile ? 5 : 5;

  // Fetch tasks data function
  const fetchTasksData = useCallback(() => {
    fetch('https://management-backend.opengpu.network/api/tasks')
      .then((res) => res.json())
      .then((data: unknown) => {
        const responseData = data as TaskResponse;
        if (responseData.status === 'success') {
          // Handle both data formats
          let tasksData: Array<Task> = [];
          if (Array.isArray(responseData.data)) {
            tasksData = responseData.data;
          } else if (responseData.tasks) {
            tasksData = responseData.tasks;
          } else {
            tasksData = responseData.data.tasks;
          }
          setTasks(tasksData.slice(0, maxDisplayCount));
        }
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, [ maxDisplayCount ]);

  useEffect(() => {
    // Set loading state before initial fetch
    setIsLoading(true);

    // Initial fetch
    fetchTasksData();

    // Set up interval to fetch data every 5 seconds
    const intervalId = setInterval(fetchTasksData, 35_000);

    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, [ fetchTasksData ]);

  let content;

  if (isLoading) {
    // Loading state
    content = <Text>Loading...</Text>;
  } else if (tasks.length === 0) {
    // Empty state
    content = <Text>No tasks found.</Text>;
  } else {
    content = (
      <VStack
        spacing={ 2 }
        mb={ 3 }
        overflow="visible"
        alignItems="stretch"
        width="100%"
        gap={ 0 }
      >
        <AnimatePresence initial={ false }>
          { tasks.map((task) => (
            <LatestTasksItem key={ task._id } task={ task } isLoading={ isLoading }/>
          )) }
        </AnimatePresence>
      </VStack>
    );
  }

  return (
    <Box width={{ base: '100%' }} overflow="hidden">
      <Flex justifyContent="space-between" alignItems="center" mb={ 3 }>
        <Heading as="h4" size="sm">
          Latest tasks
        </Heading>
        <LinkInternal
          fontSize="sm"
          href="https://management.opengpu.network/tasks"
          isExternal
        >
          View all tasks
        </LinkInternal>
      </Flex>
      <Box mt={ 3 } width="100%">
        { content }
      </Box>
    </Box>
  );
};

export default LatestTasks;
